/**
 * THE typed REST client.
 *
 * CLAUDE.md Part I §5 — all backend access goes through here. No bare `fetch`
 * in a component, ever. Authentication, error normalisation and `decision_ref`
 * surfacing are enforced in this one place; a second access path defeats all
 * three at once. The lint config enforces it mechanically.
 */

import type { ConsoleConfig } from '@/lib/env/config'

import { NetworkError, toApiError } from './errors'

export interface RequestOptions {
  readonly signal?: AbortSignal
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>
  /**
   * Caller-supplied idempotency key. Binding, and carried unchanged to the EMI.
   * REQUIRED on every mutating money-adjacent instruction (invariant 5).
   */
  readonly idempotencyKey?: string
}

/**
 * Three verbs, and there is no fourth.
 *
 * # There is no `put` and no `del`, and that is the backend's decision
 *
 * The admin surface registers **zero** PUT routes and **zero** DELETE routes.
 * That is not an omission waiting to be filled: `admin_routes.go` says so at
 * every family it registers — "NO PUT AND NO DELETE ANYWHERE, and both absences
 * are the same decision", "a published version is immutable by trigger and a
 * change is a new version", "`retire` is the operation that exists instead of
 * DELETE. It closes the live row, and the row stays".
 *
 * Both verbs were added here and taken out again in the same change. They
 * looked supported because `git grep '"DELETE"'` finds
 * `DELETE /v1/admin/referral/flags/1` — in `admin_referral_test.go`, which
 * asserts the method is REFUSED. A test naming a route it expects to 405 reads
 * exactly like a route table.
 *
 * Adding them was the more expensive mistake of the two. A client with a `del`
 * invites a screen with a delete button, and law 5 forbids that on everything
 * append-only; a client with a `put` invites an in-place edit, and law 6 says a
 * versioned entity is changed by publishing a version that closes the incumbent.
 * The backend's own comment names the failure precisely: a route implying
 * otherwise "would be a control this backend must not expose, and the console
 * would render a button for it".
 *
 * So the withdrawal operations are POSTs and they are named for what they do —
 * `/retire`, `/close`, `/resolve`, `/lift`, `/reinstate`. If one ever seems to
 * be missing, the answer is an issue on `go-wallet`, never a fourth verb here.
 */
export interface ApiClient {
  get<T>(path: string, options?: RequestOptions): Promise<T>
  post<T>(path: string, body: unknown, options?: RequestOptions): Promise<T>
  patch<T>(path: string, body: unknown, options?: RequestOptions): Promise<T>
}

/** The console only ever talks to the admin surface. */
const ADMIN_PREFIX = '/v1/admin'

/**
 * The one read this console makes with NO session (#215).
 *
 * # Why it is here and not a `fetch` in `session.ts`
 *
 * Part I §5 admits no exception: authentication, error normalisation and
 * `decision_ref` surfacing are enforced in one place, and a second access path
 * defeats all three. So it lives in the seam and reuses the same `readBody` and
 * `toApiError` as every other call.
 *
 * # Why it is a function rather than a method on `ApiClient`
 *
 * `ApiClient` is the shape every screen adapter is handed, and every one of
 * them is authenticated by definition. Adding a fourth method would offer an
 * anonymous read to eighty adapters that must never make one, and would put it
 * in every test double in the repository. Exactly one caller needs this — the
 * sign-in path, which has to learn which identity plane to send an operator to
 * BEFORE a session can exist — so it is reachable only by importing it.
 *
 * # Two deliberate differences from `get`
 *
 * NO `/v1/admin` prefix: the admin surface is authenticated by definition, and
 * an anonymous route inside it would teach a reader that the prefix means
 * nothing. And `credentials: 'omit'` — there is no session to send, and a
 * non-credentialed request can be answered with a simple CORS policy rather
 * than one that has to name this origin.
 */
export async function getAnonymous<T>(
  config: ConsoleConfig, path: string, signal?: AbortSignal,
): Promise<T> {
  if (!path.startsWith('/')) throw new Error(`api path must start with "/": ${path}`)

  let response: Response
  try {
    response = await fetch(`${config.apiBaseUrl}${path}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'omit',
      ...(signal === undefined ? {} : { signal }),
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new NetworkError(cause)
  }

  const payload = await readBody(response)
  if (!response.ok) {
    throw toApiError(response.status, payload, response.headers.get('X-Request-Id'))
  }
  return payload as T
}

function buildUrl(baseUrl: string, path: string, query: RequestOptions['query']): string {
  if (!path.startsWith('/')) throw new Error(`api path must start with "/": ${path}`)
  const url = new URL(`${baseUrl}${ADMIN_PREFIX}${path}`)
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value))
  }
  return url.toString()
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null
  const text = await response.text()
  if (text === '') return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

export function createApiClient(config: ConsoleConfig): ApiClient {
  async function request<T>(
    method: 'GET' | 'POST' | 'PATCH',
    path: string,
    body: unknown,
    options: RequestOptions = {},
  ): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    if (options.idempotencyKey !== undefined) {
      headers['Idempotency-Key'] = options.idempotencyKey
    }

    const init: RequestInit = {
      method,
      headers,
      // The staff session is an httpOnly cookie set at the edge. The console
      // holds no token of its own — CLAUDE.md Part I §6.
      credentials: 'include',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    }

    let response: Response
    try {
      response = await fetch(buildUrl(config.apiBaseUrl, path, options.query), init)
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
      throw new NetworkError(cause)
    }

    const requestId = response.headers.get('X-Request-Id')
    const payload = await readBody(response)

    if (!response.ok) throw toApiError(response.status, payload, requestId)
    return payload as T
  }

  return {
    get: <T>(path: string, options?: RequestOptions) =>
      request<T>('GET', path, undefined, options),
    post: <T>(path: string, body: unknown, options?: RequestOptions) =>
      request<T>('POST', path, body, options),
    patch: <T>(path: string, body: unknown, options?: RequestOptions) =>
      request<T>('PATCH', path, body, options),
  }
}
